import type { Feature } from 'admin/src/enums';
import { checkFeatureFlag } from 'pollworker/src/fetchers';
import { selectFeatureFlags, setFeatureFlagCheck } from 'pollworker/src/state/featureFlag';
import type { AppState } from 'pollworker/src/state/store';
import type { PollworkerCustomerFeature } from 'pollworker/src/types';
import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from './index';

export interface FeatureFlagState {
  enabled: boolean;
  loading: boolean;
}

function hasDbFeature(features: PollworkerCustomerFeature[] | undefined, dbFlag: Feature): boolean {
  return !!(features || []).find((feature) => feature.Feature === dbFlag && feature.Enabled);
}

export const useFeatureFlagState = (flag: string | null | undefined, dbFlag?: Feature | null): FeatureFlagState => {
  const dispatch = useAppDispatch();
  const flags = useAppSelector(selectFeatureFlags);
  const currentUser = useAppSelector((state: AppState) => state.currentPollworkerUser);

  useEffect(() => {
    if (!flag || flags[flag] !== undefined) return;

    checkFeatureFlag(flag).then(
      (enabled: boolean) => {
        dispatch(setFeatureFlagCheck({ name: flag, enabled }));
      },
      () => {
        dispatch(setFeatureFlagCheck({ name: flag, enabled: false }));
      },
    );
  }, [dispatch, flag, flags]);

  if (!flag && dbFlag == null) {
    throw new Error('You must provide a flag or dbFlag');
  }

  if (flag) {
    return { enabled: !!flags[flag], loading: flags[flag] === undefined };
  }

  return { enabled: hasDbFeature(currentUser?.Features, dbFlag as Feature), loading: !currentUser?.EVUserId };
};

export const useIsFeatureFlagEnabled = (flag: string | null | undefined, dbFlag?: Feature | null): boolean => {
  return useFeatureFlagState(flag, dbFlag).enabled;
};
